import React, {useContext, useMemo, useState} from 'react';
import {Box, Stack, Typography, Button, InputBase, CircularProgress, IconButton, Checkbox, MenuItem, Select, Switch} from '@mui/material';
import {HugeiconsIcon} from '@hugeicons/react';
import {Copy01Icon, PlayIcon, Alert02Icon, CheckIcon, Delete02Icon, Search01Icon, Wrench01Icon} from '@hugeicons/core-free-icons';
import {toast} from 'react-toastify';
import {Context} from '../../../../context/AppContext';
import {ConfirmModalNew} from './ConfirmModalNew';

const controlSx = {
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  borderRadius: "10px",
  bgcolor: "#FAFAFD",
  color: "#6B6B80",
  fontSize: 13,
};

const kickerSx = {fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8491F7"};
const codeSx = {
  bgcolor: "#FAFAFD", borderRadius: "14px", p: 2, m: 0, fontFamily: "monospace", fontSize: 12, lineHeight: 1.6, color: "#374151",
  whiteSpace: "pre-wrap", wordBreak: "break-word", overflow: "auto", maxHeight: 320,
};
const ghostButtonSx = {
  textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: "9px", color: "#4B55C9", minWidth: 0, px: 1.25,
  "&:hover": {bgcolor: "rgba(132, 145, 247, 0.1)"},
};

const getSchema = (tool) => tool.input_schema || tool.inputSchema || {};

// the property's JSON schema type; a nullable property ("anyOf": [{type}, {type: "null"}]) uses its non-null type
const getSchemaType = (prop) => prop?.type || prop?.anyOf?.find(t => t.type !== "null")?.type || "string";
const getEnum = (prop) => prop?.enum || prop?.anyOf?.find(t => t.enum)?.enum || null;

const getFieldType = (prop) => {
  const type = getSchemaType(prop);
  if (type === "integer" || type === "number") return "number";
  if (type === "boolean") return "boolean";
  return "text";
};

const buildDefaultArgs = (properties) => Object.fromEntries(
  Object.entries(properties).map(([key, val]) => {
    if ("default" in val) return [key, val.default ?? ""];
    const type = getFieldType(val);
    if (type === "boolean") return [key, false];
    return [key, ""];
  })
);

const toolSummary = (tool) => {
  const schema = getSchema(tool);
  const count = Object.keys(schema.properties || {}).length;
  const requiredCount = (schema.required || []).length;
  return `${count} ${count === 1 ? 'parameter' : 'parameters'}${requiredCount > 0 ? ` · ${requiredCount} required` : ''}`;
};

const copyText = async (text, message) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(message);
  } catch (err) {
    toast.error('Failed to copy');
  }
};

const Kicker = ({children, action}) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{mb: 1.25, minHeight: 28}}>
    <Typography sx={kickerSx}>{children}</Typography>
    {action}
  </Stack>
);

const ResultPanel = ({result, tookMs}) => {
  const [copied, setCopied] = useState(false);
  const [pretty, setPretty] = useState(true);

  const rawText = typeof result === 'string' ? result : JSON.stringify(result, null, 2);
  // Un-escapes \n, \t, \r, \" sequences so multi-line text renders as real line breaks instead of literal characters
  const prettyText = rawText.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '').replace(/\\"/g, '"');
  const text = pretty ? prettyText : rawText;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Result copied');
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 0.75}}>
        <Typography sx={{...kickerSx, color: '#2F9A6A'}}>
          Result{tookMs !== null && <Box component="span" sx={{ml: 1, color: '#8A8AA0', letterSpacing: 0, textTransform: 'none', fontWeight: 500}}>{tookMs} ms</Box>}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5} component="label" sx={{cursor: 'pointer'}}>
          <Checkbox
            size="small"
            checked={pretty}
            onChange={(e) => setPretty(e.target.checked)}
            sx={{p: 0.25, color: '#C9CAD8', '&.Mui-checked': {color: '#8491F7'}}}
          />
          <Typography sx={{fontSize: '12px', color: '#6B6B80', fontWeight: 500}}>Pretty print</Typography>
        </Stack>
      </Stack>
      <Box sx={{position: 'relative'}}>
        <IconButton size="small" onClick={handleCopy} sx={{position: 'absolute', top: 8, right: 8, color: '#8A8AA0', zIndex: 1}}>
          <HugeiconsIcon icon={copied ? CheckIcon : Copy01Icon} size={14}/>
        </IconButton>
        <Box component="pre" sx={{...codeSx, pr: 5, maxHeight: {xs: 320, md: 480}}}>{text}</Box>
      </Box>
    </Box>
  );
};

const TypePill = ({children}) => (
  <Box component="span" sx={{
    px: 1, py: 0.25, borderRadius: "7px", fontFamily: "monospace", fontSize: 12, color: "#4B55C9", bgcolor: "#EEF0FE",
  }}>
    {children}
  </Box>
);

const ParametersTable = ({properties, required}) => {
  const entries = Object.entries(properties);
  if (entries.length === 0) {
    return <Typography sx={{fontSize: 13, color: '#8A8AA0'}}>This tool takes no arguments.</Typography>;
  }
  return (
    <Box sx={{overflowX: 'auto', borderRadius: '14px', bgcolor: '#FAFAFD'}}>
      <Box component="table" sx={{width: '100%', minWidth: 620, borderCollapse: 'collapse', fontSize: 13}}>
        <Box component="thead">
          <Box component="tr">
            {['Name', 'Type', 'Required', 'Default', 'Description'].map(head => (
              <Box key={head} component="th" sx={{textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8A8AA0', px: 1.75, py: 1.25}}>
                {head}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {entries.map(([key, prop]) => {
            const isRequired = required.includes(key);
            const options = getEnum(prop);
            return (
              <Box component="tr" key={key} sx={{'& td': {px: 1.75, py: 1.5, verticalAlign: 'top', borderTop: '1px solid #EEEEF5'}}}>
                <Box component="td" sx={{fontFamily: 'monospace', fontWeight: 600, fontSize: 12.5, color: '#23264D'}}>{key}</Box>
                <Box component="td"><TypePill>{getSchemaType(prop)}</TypePill></Box>
                <Box component="td">
                  <Box component="span" sx={{
                    px: 1, py: 0.25, borderRadius: '999px', fontSize: 11, fontWeight: 600,
                    color: isRequired ? '#DC2626' : '#8A8AA0', bgcolor: isRequired ? 'rgba(220, 38, 38, 0.08)' : '#ECECF3',
                  }}>
                    {isRequired ? 'Required' : 'Optional'}
                  </Box>
                </Box>
                <Box component="td" sx={{fontFamily: 'monospace', fontSize: 12.5, color: '#6B6B80'}}>
                  {'default' in prop ? JSON.stringify(prop.default) : <Box component="span" sx={{color: '#B4B4C3'}}>none</Box>}
                </Box>
                <Box component="td" sx={{color: '#374151'}}>
                  {prop.description || prop.title || <Box component="span" sx={{color: '#B4B4C3'}}>No description</Box>}
                  {options && (
                    <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{mt: 0.75}}>
                      {options.map(option => (
                        <Box key={String(option)} component="span" sx={{px: 0.9, borderRadius: '6px', fontFamily: 'monospace', fontSize: 11, color: '#6B6B80', bgcolor: '#fff'}}>
                          {String(option)}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const ToolDetail = ({tool, onRequestDelete}) => {
  const {callMCPTool} = useContext(Context);
  const schema = getSchema(tool);
  const properties = schema.properties || {};
  const required = schema.required || [];
  const paramCount = Object.keys(properties).length;
  const isActive = tool.is_active ?? tool.active ?? true;

  const [fieldValues, setFieldValues] = useState(() => buildDefaultArgs(properties));
  const [fieldErrors, setFieldErrors] = useState({});
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [tookMs, setTookMs] = useState(null);
  const [error, setError] = useState(null);

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => ({...prev, [key]: value}));
    if (required.includes(key) && value === "") {
      setFieldErrors(prev => ({...prev, [key]: "Required"}));
    } else {
      setFieldErrors(prev => ({...prev, [key]: null}));
    }
  };

  const validate = () => {
    const errors = {};
    required.forEach(key => {
      const val = fieldValues[key];
      if (val === "" || val === null || val === undefined) errors[key] = "Required";
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildArgs = () => Object.fromEntries(
    Object.entries(fieldValues).map(([key, val]) => {
      const type = getFieldType(properties[key]);
      if (type === "number" && val !== "" && val !== null) return [key, Number(val)];
      if (val === "") return [key, null];
      return [key, val];
    })
  );

  const handleExecute = async () => {
    if (!validate()) return;
    setRunning(true);
    setResult(null);
    setError(null);
    const startedAt = Date.now();
    try {
      const data = await callMCPTool({server_id: tool.server_id, tool_name: tool.name, arguments: buildArgs()});
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Tool call failed");
    } finally {
      setTookMs(Date.now() - startedAt);
      setRunning(false);
    }
  };

  const toolDefinition = {name: tool.name, description: tool.description, inputSchema: schema};

  return (
    <Stack gap={3.5} sx={{bgcolor: '#fff', borderRadius: '20px', boxShadow: '0 2px 12px rgba(35, 38, 77, 0.07)', p: {xs: 2.25, md: 3}, minWidth: 0}}>
      <Stack direction="row" alignItems="flex-start" gap={1.75} flexWrap="wrap">
        <Box sx={{
          width: 48, height: 48, borderRadius: '15px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#8491F7', bgcolor: '#EEF0FE',
        }}>
          <HugeiconsIcon icon={Wrench01Icon} size={22}/>
        </Box>
        <Box sx={{flex: '1 1 220px', minWidth: 0}}>
          <Typography component="h4" sx={{fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: '#23264D', letterSpacing: '-0.01em', overflowWrap: 'anywhere'}}>
            {tool.name}
          </Typography>
          <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 0.75, px: 1.25, py: 0.25, borderRadius: '999px', fontSize: 12, fontWeight: 600,
            color: isActive ? '#2F8A62' : '#9A6B1F', bgcolor: isActive ? '#E8F5EE' : '#FBF1DF',
          }}>
            {!isActive && <HugeiconsIcon icon={Alert02Icon} size={13}/>}
            {isActive ? 'Active' : 'Inactive · may not run'}
          </Box>
        </Box>
        <Button
          startIcon={<HugeiconsIcon icon={Copy01Icon} size={15}/>}
          onClick={() => copyText(JSON.stringify(toolDefinition, null, 2), 'Tool definition copied')}
          sx={{textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px', height: 36, color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'}}}
        >
          Copy definition
        </Button>
      </Stack>

      <Box>
        <Kicker>Description</Kicker>
        <Typography sx={{fontSize: 14.5, lineHeight: 1.7, color: '#374151', maxWidth: '72ch', whiteSpace: 'pre-wrap'}}>
          {tool.description || 'This tool has no description.'}
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{mt: 1.5}}>
          {[`${paramCount} ${paramCount === 1 ? 'parameter' : 'parameters'}`, `${required.length} required`].map(label => (
            <Box key={label} component="span" sx={{px: 1.25, py: 0.4, borderRadius: '8px', fontSize: 12, color: '#6B6B80', bgcolor: '#FAFAFD'}}>{label}</Box>
          ))}
        </Stack>
      </Box>

      {!isActive && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap" sx={{p: 1.5, borderRadius: '12px', bgcolor: 'rgba(217, 161, 58, 0.1)'}}>
          <Typography sx={{fontSize: 13, color: '#9A6B1F'}}>This tool is inactive and may not execute successfully.</Typography>
          <Button
            size="small"
            startIcon={<HugeiconsIcon icon={Delete02Icon} size={13}/>}
            onClick={() => onRequestDelete?.(tool)}
            sx={{textTransform: 'none', fontWeight: 700, fontSize: 12, color: '#DC2626', flexShrink: 0, '&:hover': {backgroundColor: 'rgba(220, 38, 38, 0.08)'}}}
          >
            Delete Tool
          </Button>
        </Stack>
      )}

      <Stack gap={2}>
        <Kicker>Try it out</Kicker>
        {paramCount > 0 && (
          <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))'}}>
            {Object.entries(properties).map(([key, prop]) => {
              const isRequired = required.includes(key);
              const type = getFieldType(prop);
              const options = getEnum(prop);
              return (
                <Box key={key} sx={{minWidth: 0}}>
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{mb: 0.75}}>
                    <Typography sx={{fontSize: 12.5, fontFamily: 'monospace', fontWeight: 600, color: '#23264D'}}>{key}</Typography>
                    {isRequired && <Typography component="span" sx={{color: '#DC2626', fontSize: 12.5, fontWeight: 700}}>*</Typography>}
                    <Typography sx={{fontSize: 11.5, color: '#8A8AA0'}}>{getSchemaType(prop)}</Typography>
                  </Stack>
                  {type === 'boolean' ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{height: 40}}>
                      <Switch
                        checked={Boolean(fieldValues[key])}
                        onChange={(e) => handleFieldChange(key, e.target.checked)}
                        inputProps={{'aria-label': key}}
                        sx={{'& .MuiSwitch-switchBase.Mui-checked': {color: '#8491F7'}, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {bgcolor: '#8491F7'}}}
                      />
                      <Typography sx={{fontSize: 13, color: '#6B6B80'}}>{String(Boolean(fieldValues[key]))}</Typography>
                    </Stack>
                  ) : options ? (
                    <Select
                      fullWidth displayEmpty variant="standard" disableUnderline
                      value={fieldValues[key] ?? ""}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      inputProps={{'aria-label': key}}
                      sx={{...controlSx, '& .MuiSelect-select': {py: 0, pr: '22px !important', fontFamily: 'monospace', fontSize: 13, color: '#23264D'}}}
                      MenuProps={{PaperProps: {sx: {borderRadius: '12px', mt: 0.5}}}}
                    >
                      {!isRequired && <MenuItem value=""><em>None</em></MenuItem>}
                      {options.map(option => <MenuItem key={String(option)} value={option}>{String(option)}</MenuItem>)}
                    </Select>
                  ) : (
                    <Box sx={{...controlSx, ...(fieldErrors[key] && {boxShadow: 'inset 0 0 0 1.5px #DC2626'})}}>
                      <InputBase
                        type={type === 'number' ? 'number' : 'text'}
                        value={fieldValues[key] ?? ""}
                        onChange={e => handleFieldChange(key, e.target.value)}
                        placeholder={isRequired ? "Required" : "Optional"}
                        inputProps={{"aria-label": key}}
                        fullWidth
                        sx={{flex: 1, fontSize: 13, fontFamily: 'monospace', color: "#23264D"}}
                      />
                    </Box>
                  )}
                  {fieldErrors[key]
                    ? <Typography sx={{fontSize: 11.5, color: '#DC2626', mt: 0.5}}>{fieldErrors[key]}</Typography>
                    : prop.description && <Typography sx={{fontSize: 11.5, color: '#8A8AA0', mt: 0.5}}>{prop.description}</Typography>}
                </Box>
              );
            })}
          </Box>
        )}

        {error && (
          <Box sx={{p: 1.5, borderRadius: '10px', bgcolor: 'rgba(220, 38, 38, 0.08)'}}>
            <Typography sx={{fontSize: 12, color: '#DC2626'}}>{typeof error === 'string' ? error : JSON.stringify(error)}</Typography>
          </Box>
        )}

        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
          <Button
            disabled={running || !isActive}
            startIcon={running ? <CircularProgress size={14} sx={{color: '#fff'}}/> : <HugeiconsIcon icon={PlayIcon} size={15}/>}
            onClick={handleExecute}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: 14, borderRadius: '14px', bgcolor: '#8491F7', color: '#fff', px: 3, height: 44,
              boxShadow: '0 4px 14px rgba(132, 145, 247, 0.32)',
              transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, box-shadow 0.2s ease',
              '& .MuiButton-startIcon': {mr: 0.75},
              '&:hover': {bgcolor: '#6B78E8', transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(132, 145, 247, 0.4)'},
              '&:active': {transform: 'translateY(0)'},
              '&.Mui-disabled': {bgcolor: '#F2F2F7', color: '#B4B4C3', boxShadow: 'none'},
            }}
          >
            {running ? "Running..." : "Run tool"}
          </Button>
          {!isActive && <Typography sx={{fontSize: 12, color: '#8A8AA0'}}>Inactive tools cannot be run.</Typography>}
        </Stack>

        {result !== null && <ResultPanel result={result} tookMs={tookMs}/>}
      </Stack>

      <Box>
        <Kicker>Parameters</Kicker>
        <ParametersTable properties={properties} required={required}/>
      </Box>

      <Box>
        <Kicker action={
          <Stack direction="row" gap={0.5}>
            {schemaOpen && <Button size="small" onClick={() => copyText(JSON.stringify(schema, null, 2), 'Schema copied')} sx={ghostButtonSx}>Copy</Button>}
            <Button size="small" onClick={() => setSchemaOpen(v => !v)} sx={ghostButtonSx}>{schemaOpen ? 'Hide' : 'Show'} JSON</Button>
          </Stack>
        }>
          Input schema
        </Kicker>
        {schemaOpen && <Box component="pre" sx={codeSx}>{JSON.stringify(schema, null, 2)}</Box>}
      </Box>
    </Stack>
  );
};

export const MCPToolsSectionNew = ({ server, onToolDeleted }) => {
  const {deleteInactiveTool} = useContext(Context);
  const tools = useMemo(() => server?.tools || [], [server?.tools]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteInactiveTool(pendingDelete.id);
      toast.success('Tool deleted');
      setPendingDelete(null);
      await onToolDeleted?.();
    } catch (err) {
      toast.error('Failed to delete tool');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  if (tools.length === 0) {
    return (<Box sx={{textAlign: 'center', color: '#8A8AA0', py: 6}}><Typography sx={{fontSize: '14px'}}>No tools available for this server</Typography></Box>);
  }

  const toolKey = (tool, index) => `${server?.id}-${tool.id || tool.name || index}`;
  const term = search.trim().toLowerCase();
  const rows = tools
    .map((tool, index) => ({tool, key: toolKey(tool, index)}))
    .filter(({tool}) => !term || tool.name?.toLowerCase().includes(term) || tool.description?.toLowerCase().includes(term));
  const selected = rows.find(row => row.key === selectedKey) || rows[0];

  return (
    <Box sx={{
      display: 'grid', gap: 2, gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: '280px minmax(0, 1fr)'},
      height: {md: 'calc(100vh - 140px)'}, minHeight: {md: 560}, alignItems: 'stretch',
    }}>
      <Box sx={{display: {xs: 'block', md: 'none'}}}>
        <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8491F7', mb: 0.75}}>
          Choose a tool · {tools.length}
        </Typography>
        <Select
          fullWidth variant="standard" disableUnderline
          value={selected?.key || ''}
          onChange={(e) => setSelectedKey(e.target.value)}
          inputProps={{'aria-label': 'Choose a tool'}}
          renderValue={() => selected && (
            <Box sx={{minWidth: 0}}>
              <Typography noWrap sx={{fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: '#23264D'}}>{selected.tool.name}</Typography>
              <Typography noWrap sx={{fontSize: 11.5, color: '#8A8AA0'}}>{toolSummary(selected.tool)}</Typography>
            </Box>
          )}
          sx={{
            bgcolor: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(35, 38, 77, 0.07)', px: 1.75, py: 1,
            '& .MuiSelect-select': {py: 0.25, pr: '28px !important'}, '& .MuiSelect-icon': {right: 12, color: '#8491F7'},
          }}
          MenuProps={{PaperProps: {sx: {borderRadius: '14px', mt: 0.75, maxHeight: '60vh'}}}}
        >
          {rows.map(({tool, key}) => (
            <MenuItem key={key} value={key} sx={{display: 'block', py: 1, '&.Mui-selected': {bgcolor: 'rgba(132, 145, 247, 0.14)'}}}>
              <Typography noWrap sx={{fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: '#23264D'}}>{tool.name}</Typography>
              <Typography noWrap sx={{fontSize: 11, color: '#8A8AA0'}}>{toolSummary(tool)}{(tool.is_active ?? tool.active ?? true) ? '' : ' · inactive'}</Typography>
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{bgcolor: '#fff', borderRadius: '18px', boxShadow: '0 2px 12px rgba(35, 38, 77, 0.07)', p: 1.5, minWidth: 0, minHeight: 0, display: {xs: 'none', md: 'flex'}, flexDirection: 'column'}}>
        <Box sx={{...controlSx, minHeight: 38, mb: 1}}>
          <HugeiconsIcon icon={Search01Icon} size={15}/>
          <InputBase
            placeholder="Filter tools"
            value={search}
            onChange={e => setSearch(e.target.value)}
            inputProps={{"aria-label": "Filter tools"}}
            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        <Stack
          role="tablist" aria-label="Tools" gap={0.25}
          sx={{flexDirection: {xs: 'row', md: 'column'}, overflowX: {xs: 'auto', md: 'hidden'}, overflowY: {xs: 'hidden', md: 'auto'}, flex: {md: 1}, minHeight: 0, pb: {xs: 0.5, md: 0}}}
        >
          {rows.length === 0 && <Typography sx={{fontSize: 13, color: '#8A8AA0', textAlign: 'center', py: 3, px: 1}}>No tools match</Typography>}
          {rows.map(({tool, key}) => {
            const schema = getSchema(tool);
            const count = Object.keys(schema.properties || {}).length;
            const requiredCount = (schema.required || []).length;
            const active = tool.is_active ?? tool.active ?? true;
            const isSelected = selected?.key === key;
            return (
              <Box
                key={key} component="button" type="button" role="tab" aria-selected={isSelected}
                onClick={() => setSelectedKey(key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, width: {md: '100%'}, flex: {xs: '0 0 auto', md: 'none'}, textAlign: 'left',
                  px: 1.5, py: 1.25, border: 0, borderRadius: '12px', cursor: 'pointer', font: 'inherit',
                  bgcolor: isSelected ? 'rgba(132, 145, 247, 0.14)' : 'transparent', transition: 'background-color .15s ease',
                  '&:hover': {bgcolor: isSelected ? 'rgba(132, 145, 247, 0.14)' : '#FAFAFD'},
                }}
              >
                <Box sx={{minWidth: 0}}>
                  <Typography noWrap sx={{fontFamily: 'monospace', fontSize: 12.5, fontWeight: 600, color: isSelected ? '#4B55C9' : '#23264D'}}>{tool.name}</Typography>
                  <Typography noWrap sx={{fontSize: 11, color: '#8A8AA0'}}>
                    {count} {count === 1 ? 'parameter' : 'parameters'}{requiredCount > 0 ? ` · ${requiredCount} required` : ''}
                  </Typography>
                </Box>
                {!active && <Box component="span" title="Inactive" sx={{ml: 'auto', width: 8, height: 8, borderRadius: '50%', bgcolor: '#D9A13A', flexShrink: 0}}/>}
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{minWidth: 0, minHeight: 0, overflowY: {md: 'auto'}, pr: {md: 0.5}}}>
        {selected && (
          <ToolDetail
            key={selected.key}
            tool={{...selected.tool, server_id: selected.tool.server_id ?? server?.id}}
            onRequestDelete={setPendingDelete}
          />
        )}
      </Box>

      <ConfirmModalNew
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title={`Delete "${pendingDelete?.name}"?`}
        description="This inactive tool will be permanently removed from the server."
        confirmText="Delete Tool"
        loading={deleting}
      />
    </Box>
  );
};
